import { BN_AUTH_CHANGE, fetchMe } from "@bn-games/auth-client";
import { listInvites, startLobbyConnection, type Invite } from "@bn-games/social-client";

// Returning to a tab commonly fires `focus` AND `visibilitychange` for the one
// user action, so both are listened to and coalesced by time. Deliberately not
// an interval: the lobby socket's INVITE_PUSH is the live path, and this wake
// is the fallback for a push that never arrives.
const WAKE_COALESCE_MS = 1000;

export interface InviteFeedOptions {
  /** WS URL of `/ws/lobby`; starts (or joins) the page's shared connection. */
  readonly serverUrl?: string;
  /** The full pending list after each successful fetch; `[]` once signed out. */
  readonly onInvites: (invites: readonly Invite[]) => void;
}

export interface InviteFeed {
  destroy(): void;
}

/**
 * Keeps the pending-invite list fresh: on start, on every pushed invite, on
 * auth changes and on a coalesced focus/visibility wake. Signed-out visitors
 * never reach `GET /invites` — the `fetchMe` check comes first and rides its
 * shared profile cache.
 */
export function createInviteFeed(options: InviteFeedOptions): InviteFeed {
  let destroyed = false;
  let inFlight = false;
  let rerun = false;
  let lastWakeAt = Date.now();

  async function refresh(): Promise<void> {
    if (destroyed) return;
    // A push landing mid-refresh must not be lost to the in-flight guard.
    if (inFlight) {
      rerun = true;
      return;
    }
    inFlight = true;
    try {
      const profile = await fetchMe();
      if (destroyed) return;
      if (profile === null) {
        options.onInvites([]);
        return;
      }
      const result = await listInvites();
      if (destroyed || !result.ok) return;
      options.onInvites(result.value);
    } finally {
      inFlight = false;
      if (rerun && !destroyed) {
        rerun = false;
        void refresh();
      }
    }
  }

  function onWake(): void {
    if (document.visibilityState === "hidden") return;
    const now = Date.now();
    if (now - lastWakeAt < WAKE_COALESCE_MS) return;
    lastWakeAt = now;
    void refresh();
  }
  const onAuthChange = (): void => void refresh();

  const stopPush =
    options.serverUrl === undefined
      ? (): void => {}
      : startLobbyConnection({ serverUrl: options.serverUrl }).onInvitePush(() => void refresh());
  window.addEventListener("focus", onWake);
  document.addEventListener("visibilitychange", onWake);
  window.addEventListener(BN_AUTH_CHANGE, onAuthChange);
  void refresh();

  return {
    destroy(): void {
      destroyed = true;
      stopPush();
      window.removeEventListener("focus", onWake);
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener(BN_AUTH_CHANGE, onAuthChange);
    },
  };
}
